import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../../store/ducks/report";
// Components
import {
  Checkbox,
  Col,
  DatePicker,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  notification,
} from "antd";
import NumberFormat from "react-number-format";
import CustomDivider from "../../utils/customDivider";
import {
  Container,
  ExtensionButton,
  ExtensionContainer,
  Title,
} from "./styles";
// Services
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { getCattleProducersDropdown } from "../../../services/cattleProducerService";
import { processReportContractsSaleResult } from "../../../services/reportService";
import ExportPdfIcon from "../../utils/icons/export/pdf";
import fileReader from "../../../utils/fileReader";
import { momentTimeZoneHour } from "../../../utils/dateUtils";

const INITIAL_FORM_STATE = {
  startDate: null,
  endDate: null,
  contractType: "ARROBA_PRODUCTION",
  cattleProducerId: null,
  dailyOperationalCostMode: "BOVEXO_COSTING",
  dailyOperationalCostValue: null,
  additionalSlaughterCostValue: null,
  splitByOriginLot: true,
};

const validationSchema = Yup.object().shape({
  startDate: Yup.date().nullable().required(),
  endDate: Yup.date().nullable().required(),
  contractType: Yup.string().nullable().required(),
  cattleProducerId: Yup.string().nullable(),
  dailyOperationalCostMode: Yup.string().required(),
  dailyOperationalCostValue: Yup.number()
    .nullable()
    .notRequired()
    .when(["dailyOperationalCostMode"], {
      is: (mode) => mode === "USER_DEFINED",
      then: Yup.number().min(0.01).required(),
      otherwise: Yup.number().nullable().notRequired(),
    }),
  additionalSlaughterCostValue: Yup.number().nullable().notRequired(),
  splitByOriginLot: Yup.boolean().required(),
});

const ExportReportContractsSaleResultModal = () => {
  const formikRef = useRef();
  const [cattleProducerOptions, setCattleProducerOptions] = useState([]);
  const [isLoadingCattleProducers, setIsLoadingCattleProducers] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { isModalContractsSaleResultReportVisible: modalVisible } = useSelector(
    (state) => state.report,
  );

  const dispatch = useDispatch();

  const fetchCattleProducers = useCallback(async () => {
    setIsLoadingCattleProducers(true);
    try {
      const response = await getCattleProducersDropdown({ groupId, farmId });
      const data = response?.data;
      const all = data?.results ?? (Array.isArray(data) ? data : []);
      setCattleProducerOptions(all);
    } catch (error) {
    } finally {
      setIsLoadingCattleProducers(false);
    }
  }, [groupId, farmId]);

  async function handleSubmit(values) {
    setIsLoading(true);

    notification.info({
      message:
        "O seu relatório está sendo preparado e logo será feito o download!",
    });

    const requestDate = momentTimeZoneHour(new Date()).format("YYYYMMDDHHmmss");

    const body = {
      startDate: moment(values.startDate).format("YYYY-MM-DD"),
      endDate: moment(values.endDate).format("YYYY-MM-DD"),
      contractType: values.contractType,
      cattleProducerId: values.cattleProducerId || null,
      dailyOperationalCostMode: values.dailyOperationalCostMode,
      dailyOperationalCostValue:
        values.dailyOperationalCostMode === "USER_DEFINED"
          ? values.dailyOperationalCostValue
          : null,
      additionalSlaughterCostValue: values.additionalSlaughterCostValue ?? null,
      splitByOriginLot: values.splitByOriginLot,
    };

    setTimeout(() => {
      setIsLoading(false);
      dispatch(ReportActions.hideModal());
    }, 100);

    try {
      const { data } = await processReportContractsSaleResult({
        groupId,
        farmId,
        body,
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `Resultado_Venda_Contratos_${requestDate}.zip`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: translation.successGenerateReport,
      });
    } catch (error) {
      let message = translation.errorGenerateReport;
      if (error?.response) {
        try {
          const { data } = error.response;
          const file = await fileReader(data);
          if (typeof file === "string") {
            const result = JSON.parse(file);
            if (result.code === 5035) {
              message =
                translation.contractsSaleResultReportModal.messages
                  .errorNoDataFound;
            }
          }
        } catch (readError) {}
      }
      notification.error({ message });
    }
  }

  useEffect(() => {
    if (formikRef && formikRef.current && modalVisible) {
      formikRef.current.resetForm(INITIAL_FORM_STATE);
    }
    return () => {};
  }, [modalVisible]);

  useEffect(() => {
    if (modalVisible) {
      fetchCattleProducers();
    }
    return () => {};
  }, [fetchCattleProducers, modalVisible]);

  return (
    <Container
      width={550}
      visible={modalVisible}
      centered
      closable={true}
      footer={null}
      onCancel={() => dispatch(ReportActions.hideModal())}
    >
      <Spin spinning={isLoading}>
        <Row type="flex">
          <Col span={24}>
            <Title>{translation.contractsSaleResultReportModal.title}</Title>
          </Col>
        </Row>
        <Formik
          ref={formikRef}
          initialValues={INITIAL_FORM_STATE}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, errors, submitCount }) => (
            <Form>
              {/* a. Período de Vendas */}
              <Row gutter={16}>
                <Col span={24}>
                  <label
                    className={
                      (errors.startDate || errors.endDate) && submitCount > 0
                        ? "error"
                        : ""
                    }
                  >
                    {
                      translation.contractsSaleResultReportModal.form
                        .salesPeriod
                    }{" "}
                    *
                  </label>
                  <Row type="flex" gutter={8} align="middle">
                    <Col span={11}>
                      <DatePicker
                        value={values.startDate || null}
                        format="DD/MM/YYYY"
                        placeholder={translation.defaultDatePickerPlaceholder}
                        style={{ width: "100%" }}
                        disabledDate={(current) => current.isAfter(moment())}
                        onChange={(date) => {
                          setFieldValue("startDate", date);
                          if (
                            date &&
                            values.endDate &&
                            date.isAfter(values.endDate)
                          ) {
                            setFieldValue("endDate", null);
                          }
                        }}
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      até
                    </Col>
                    <Col span={11}>
                      <DatePicker
                        value={values.endDate || null}
                        format="DD/MM/YYYY"
                        placeholder={translation.defaultDatePickerPlaceholder}
                        style={{ width: "100%" }}
                        disabledDate={(current) =>
                          current.isAfter(moment()) ||
                          (values.startDate &&
                            current.isBefore(values.startDate, "day"))
                        }
                        onChange={(date) => setFieldValue("endDate", date)}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              {/* b. Tipo Contrato */}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <label
                    className={
                      errors.contractType && submitCount > 0 ? "error" : ""
                    }
                  >
                    {
                      translation.contractsSaleResultReportModal.form
                        .contractType
                    }{" "}
                    *
                  </label>
                  <Select
                    value={values.contractType || undefined}
                    onChange={(val) => setFieldValue("contractType", val)}
                    style={{ width: "100%" }}
                    placeholder={translation.defaultSelectPlaceholder}
                  >
                    <Select.Option value="ARROBA_PRODUCTION">
                      {
                        translation.contractsSaleResultReportModal.form
                          .contractTypeOptions.ARROBA_PRODUCTION
                      }
                    </Select.Option>
                  </Select>
                </Col>
              </Row>
              {/* c. Parceiro */}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <label>
                    {
                      translation.contractsSaleResultReportModal.form
                        .cattleProducerId
                    }
                  </label>
                  <Select
                    loading={isLoadingCattleProducers}
                    value={values.cattleProducerId || undefined}
                    onChange={(val) => setFieldValue("cattleProducerId", val)}
                    allowClear
                    onDeselect={() => setFieldValue("cattleProducerId", null)}
                    style={{ width: "100%" }}
                    placeholder={translation.defaultSelectPlaceholder}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {cattleProducerOptions.map((p) => (
                      <Select.Option key={p.id} value={p.id}>
                        {p.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              {/* d. Diária Operacional */}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <label>
                    {
                      translation.contractsSaleResultReportModal.form
                        .dailyOperationalCostMode
                    }
                  </label>
                  <Row type="flex" gutter={8} align="middle">
                    <Col>
                      <Radio.Group
                        value={values.dailyOperationalCostMode}
                        onChange={(e) => {
                          setFieldValue(
                            "dailyOperationalCostMode",
                            e.target.value,
                          );
                          if (e.target.value !== "USER_DEFINED") {
                            setFieldValue("dailyOperationalCostValue", null);
                          }
                        }}
                      >
                        <Radio value="BOVEXO_COSTING">
                          {
                            translation.contractsSaleResultReportModal.form
                              .dailyOperationalCostModeOptions.BOVEXO_COSTING
                          }
                        </Radio>
                        <Radio value="USER_DEFINED">
                          {
                            translation.contractsSaleResultReportModal.form
                              .dailyOperationalCostModeOptions.USER_DEFINED
                          }
                        </Radio>
                      </Radio.Group>
                    </Col>
                    {values.dailyOperationalCostMode === "USER_DEFINED" && (
                      <Col span={8}>
                        <NumberFormat
                          customInput={Input}
                          value={
                            values.dailyOperationalCostValue != null
                              ? values.dailyOperationalCostValue
                              : ""
                          }
                          className={
                            errors.dailyOperationalCostValue && submitCount > 0
                              ? "error"
                              : ""
                          }
                          decimalScale={2}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale
                          allowNegative={false}
                          addonBefore="R$ "
                          placeholder={translation.defaultPlaceholder}
                          onValueChange={({ floatValue }) =>
                            setFieldValue(
                              "dailyOperationalCostValue",
                              floatValue != null ? floatValue : null,
                            )
                          }
                        />
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
              {/* e. Custos Adicionais de Abate */}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <label>
                    {
                      translation.contractsSaleResultReportModal.form
                        .additionalSlaughterCostValue
                    }
                  </label>
                  <NumberFormat
                    customInput={Input}
                    value={
                      values.additionalSlaughterCostValue != null
                        ? values.additionalSlaughterCostValue
                        : ""
                    }
                    decimalScale={2}
                    decimalSeparator=","
                    thousandSeparator="."
                    fixedDecimalScale
                    allowNegative={false}
                    addonBefore="R$ "
                    placeholder={translation.defaultPlaceholder}
                    onValueChange={({ floatValue }) =>
                      setFieldValue(
                        "additionalSlaughterCostValue",
                        floatValue != null ? floatValue : null,
                      )
                    }
                  />
                </Col>
              </Row>
              {/* f. Separar por Lote de Origem */}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Checkbox
                    checked={values.splitByOriginLot}
                    onChange={(e) =>
                      setFieldValue("splitByOriginLot", e.target.checked)
                    }
                  >
                    {
                      translation.contractsSaleResultReportModal.form
                        .splitByOriginLot
                    }
                  </Checkbox>
                </Col>
              </Row>
              <CustomDivider dashed title="EXTENSÃO" />
              <Row type="flex">
                <Col span={24}>
                  <ExtensionContainer>
                    <ExtensionButton type="submit">
                      <ExportPdfIcon />
                    </ExtensionButton>
                  </ExtensionContainer>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Spin>
    </Container>
  );
};

export default ExportReportContractsSaleResultModal;
